import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, Instagram, MessageCircle, ExternalLink, Mail, Award } from "lucide-react";
import { toast } from "sonner";

export default function AdminSellers() {
  const { user } = useAuth();
  const [data, setData] = useState({ sellers: [], count: 0, total_revenue: 0, total_commission: 0, total_pending_payouts: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async (search = "") => {
    setLoading(true);
    const { data } = await api.get("/admin/sellers" + (search ? `?search=${encodeURIComponent(search)}` : ""));
    setData(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (!user || user.role !== "admin") {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Admin only.</div>;
  }

  const resendWelcome = async (s) => {
    if (!window.confirm(`Resend the welcome email to ${s.business_name || s.name} (${s.email})?`)) return;
    try {
      await api.post(`/admin/sellers/${s.id}/resend-welcome`);
      toast.success(`Welcome email sent to ${s.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };

  const toggleFounding = async (s) => {
    const grant = !s.is_founding;
    const label = s.business_name || s.name;
    if (grant && !s.ready_to_promote) {
      toast.error(`${label} has only completed ${s.completed_steps}/${s.total_steps} onboarding steps. Missing: ${(s.missing_steps || []).join(", ")}.`);
      return;
    }
    if (!window.confirm(`${grant ? "Grant" : "Remove"} the Founding Seller badge for ${label}?`)) return;
    try {
      const { data } = await api.put(`/admin/sellers/${s.id}/founding`, { is_founding: grant });
      if (data.warning) toast.warning(data.warning);
      else toast.success(`${label} is ${grant ? "now a Founding Seller" : "no longer a Founding Seller"}`);
      load(q);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const visibleSellers = (data.sellers || []).filter((s) => {
    if (filter === "ready") return s.ready_to_promote;
    if (filter === "incomplete") return !s.ready_to_promote;
    if (filter === "founding") return s.is_founding;
    return true;
  });

  return (
    <div data-testid="admin-sellers" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Admin · Sellers</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Seller directory</h1>
      <p className="text-muted-foreground mt-2 text-sm">Everyone with a shop on Alteza — searchable, with live stats.</p>

      {/* Stats cards */}
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          ["Total sellers", data.count, ""],
          ["Total revenue", `$${data.total_revenue.toFixed(2)}`, "across all paid orders"],
          ["Commission earned (8%)", `$${data.total_commission.toFixed(2)}`, "your platform share"],
          ["Pending payouts", `$${(data.total_pending_payouts || 0).toFixed(2)}`, "owed to unconnected sellers"],
        ].map(([label, value, sub]) => (
          <div key={label} className="border border-border p-6 rounded-sm bg-card">
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium">{label}</div>
            <div className="font-serif text-3xl mt-2">{value}</div>
            {sub && <div className="text-xs text-muted-foreground italic mt-1">{sub}</div>}
          </div>
        ))}
      </div>

      {/* Search + filter */}
      <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
        <form
          onSubmit={(e) => { e.preventDefault(); load(q); }}
          className="flex items-center gap-3 border-b border-border focus-within:border-primary max-w-md flex-1 transition-colors"
        >
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            data-testid="admin-sellers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by shop name, name, or email…"
            className="bg-transparent outline-none text-sm py-3 flex-1 placeholder:text-muted-foreground"
          />
        </form>
        <div className="flex items-center gap-1 text-xs" data-testid="admin-sellers-filter">
          {[
            ["all", "All"],
            ["ready", "Ready to promote"],
            ["incomplete", "Incomplete"],
            ["founding", "Founding"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-sm tracking-wider uppercase text-[10px] font-semibold transition-colors ${filter === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary border border-border"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="mt-10 text-muted-foreground">Loading…</div>
      ) : data.sellers.length === 0 ? (
        <div className="mt-10 text-muted-foreground italic">No sellers found.</div>
      ) : (
        <div className="mt-8 border border-border rounded-sm overflow-hidden bg-card">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-secondary/50 text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            <div className="col-span-4">Shop / Seller</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-1 text-right">Pieces</div>
            <div className="col-span-1 text-right">Orders</div>
            <div className="col-span-2 text-right">Revenue · 8%</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          <div className="divide-y divide-border">
            {visibleSellers.length === 0 ? (
              <div className="px-6 py-10 text-muted-foreground italic text-sm">No sellers match this filter.</div>
            ) : visibleSellers.map((s) => (
              <div key={s.id} data-testid={`seller-row-${s.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 items-center hover:bg-secondary/30 transition-colors">
                <div className="md:col-span-4">
                  <div className="flex items-center gap-2">
                    <div className="font-serif text-lg">{s.business_name || s.name}</div>
                    {s.is_founding && (
                      <span className="founding-badge text-[9px] tracking-[0.1em] uppercase font-semibold px-1.5 py-0.5 rounded-sm">Founding</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground italic">Owner: {s.name}</div>
                  <div className="mt-1.5">
                    {s.ready_to_promote ? (
                      <span data-testid={`seller-status-${s.id}`} className="inline-block text-[9px] tracking-[0.1em] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-green-100 text-green-800">
                        Ready to promote
                      </span>
                    ) : (
                      <span data-testid={`seller-status-${s.id}`} title={`Missing: ${(s.missing_steps || []).join(", ")}`} className="inline-block text-[9px] tracking-[0.1em] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">
                        Incomplete {s.completed_steps}/{s.total_steps}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">Joined {new Date(s.created_at).toLocaleDateString()}</div>
                  {s.agreement_accepted_at ? (
                    <div className="text-[10px] text-primary mt-1 tracking-wider uppercase font-medium" data-testid={`seller-agreement-${s.id}`}>
                      ✓ Agreement {s.agreement_version} · {new Date(s.agreement_accepted_at).toLocaleDateString()}
                    </div>
                  ) : (
                    <div className="text-[10px] text-muted-foreground mt-1 italic">No agreement on file</div>
                  )}
                  {s.stripe_connected ? (
                    s.stripe_payouts_enabled ? (
                      <div className="text-[10px] text-green-700 mt-1 tracking-wider uppercase font-medium" data-testid={`seller-payouts-${s.id}`}>✓ Payouts active</div>
                    ) : (
                      <div className="text-[10px] text-amber-700 mt-1 tracking-wider uppercase font-medium" data-testid={`seller-payouts-${s.id}`}>◦ Stripe onboarding incomplete</div>
                    )
                  ) : (
                    <div className="text-[10px] text-muted-foreground mt-1 italic" data-testid={`seller-payouts-${s.id}`}>Not connected to Stripe</div>
                  )}
                </div>
                <div className="md:col-span-3 text-sm">
                  <a href={`mailto:${s.email}`} className="hover:text-primary transition-colors break-all">{s.email}</a>
                  {s.instagram && (
                    <a href={`https://instagram.com/${s.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mt-1">
                      <Instagram className="w-3 h-3" /> @{s.instagram.replace(/^@/, "")}
                    </a>
                  )}
                </div>
                <div className="md:col-span-1 md:text-right text-sm">
                  <span className="md:hidden text-xs text-muted-foreground mr-2">Pieces:</span>{s.product_count}
                </div>
                <div className="md:col-span-1 md:text-right text-sm">
                  <span className="md:hidden text-xs text-muted-foreground mr-2">Orders:</span>{s.paid_orders}
                </div>
                <div className="md:col-span-2 md:text-right">
                  <div className="text-sm font-medium">${s.revenue.toFixed(2)}</div>
                  <div className="text-[10px] text-primary tracking-wider uppercase">${s.commission_owed.toFixed(2)} commission</div>
                  {s.pending_payout > 0 && (
                    <div className="text-[10px] text-amber-700 tracking-wider uppercase" data-testid={`seller-pending-payout-${s.id}`}>${s.pending_payout.toFixed(2)} to pay out</div>
                  )}
                </div>
                <div className="md:col-span-1 md:text-right flex md:justify-end gap-2">
                  <button
                    onClick={() => toggleFounding(s)}
                    disabled={!s.is_founding && !s.ready_to_promote}
                    title={s.is_founding ? "Remove Founding Seller badge" : (s.ready_to_promote ? "Grant Founding Seller badge" : `Complete all ${s.total_steps} onboarding steps first`)}
                    data-testid={`toggle-founding-${s.id}`}
                    className={`p-2 transition-colors ${s.is_founding ? "text-primary" : (s.ready_to_promote ? "text-muted-foreground hover:text-primary" : "text-muted-foreground/40 cursor-not-allowed")}`}
                  >
                    <Award className="w-4 h-4" fill={s.is_founding ? "currentColor" : "none"} />
                  </button>
                  <Link to={`/shop/${s.id}`} title="View shop" className="p-2 hover:text-primary transition-colors">
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                  <Link to={`/messages/${s.id}`} title="Message" className="p-2 hover:text-primary transition-colors">
                    <MessageCircle className="w-4 h-4" />
                  </Link>
                  <button onClick={() => resendWelcome(s)} title="Resend welcome email" data-testid={`resend-welcome-${s.id}`} className="p-2 hover:text-primary transition-colors">
                    <Mail className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
